/**
 * Sydney's Plant Market — Theme JS
 * Handles: sticky header, mobile nav, search panel dropdown,
 *           testimonials slider, quick-add, back to top, cookie bar
 */

(function () {
  'use strict';

  // =============================================
  // SCROLL-AWARE HEADER
  // Fixed position — hides on scroll down, reveals minimised on scroll up
  // =============================================
  const header = document.getElementById('SiteHeader');

  if (header) {
    let lastScrollY = window.scrollY;

    function syncBodyOffset() {
      document.body.style.paddingTop = header.offsetHeight + 'px';
    }

    syncBodyOffset();
    window.addEventListener('resize', syncBodyOffset, { passive: true });

    const onScroll = () => {
      const currentY  = window.scrollY;
      const atTop     = currentY < 40;
      const goingDown = currentY > lastScrollY;
      const wasScrolled = header.classList.contains('is-scrolled');

      header.classList.toggle('is-scrolled', !atTop);

      // Re-measure offset when the header changes between full and compact
      if (wasScrolled !== !atTop) setTimeout(syncBodyOffset, 350);

      if (atTop) {
        header.classList.remove('is-hidden');
      } else if (goingDown && currentY > 80) {
        header.classList.add('is-hidden');
      } else if (!goingDown) {
        header.classList.remove('is-hidden');
      }

      lastScrollY = currentY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // =============================================
  // MOBILE NAV
  // =============================================
  const mobileToggle  = document.getElementById('MobileToggle');
  const mobileClose   = document.getElementById('MobileClose');
  const mobileNav     = document.getElementById('MobileNav');
  const mobileOverlay = document.getElementById('MobileNavOverlay');

  function openMobileNav() {
    mobileNav.classList.add('is-open');
    mobileNav.setAttribute('aria-hidden', 'false');
    mobileOverlay.classList.add('is-open');
    mobileOverlay.setAttribute('aria-hidden', 'false');
    mobileToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    mobileClose.focus();
  }

  function closeMobileNav() {
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    mobileOverlay.classList.remove('is-open');
    mobileOverlay.setAttribute('aria-hidden', 'true');
    mobileToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    mobileToggle.focus();
  }

  if (mobileToggle) mobileToggle.addEventListener('click', openMobileNav);
  if (mobileClose)  mobileClose.addEventListener('click', closeMobileNav);
  if (mobileOverlay) mobileOverlay.addEventListener('click', closeMobileNav);

  // Keyboard: close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (mobileNav && mobileNav.classList.contains('is-open')) closeMobileNav();
      if (searchPanel && searchPanel.classList.contains('is-open')) closeSearch();
    }
  });

  // Mobile accordion submenus
  document.querySelectorAll('.js-mobile-accordion').forEach((row) => {
    const toggle = row.querySelector('.mobile-nav__toggle-icon');
    const submenu = row.nextElementSibling;

    if (!toggle || !submenu) return;

    row.addEventListener('click', (e) => {
      // Only toggle if clicking the icon or the row itself (not the link)
      if (e.target.closest('a') && !e.target.closest('.mobile-nav__toggle-icon')) return;

      const isOpen = submenu.classList.contains('is-open');

      // Close all others
      document.querySelectorAll('.mobile-nav__submenu.is-open').forEach((s) => {
        s.classList.remove('is-open');
        s.previousElementSibling.classList.remove('is-open');
        s.previousElementSibling.querySelector('.mobile-nav__toggle-icon button, .mobile-nav__toggle-icon')
          ?.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        submenu.classList.add('is-open');
        row.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // =============================================
  // SEARCH PANEL (slide-down dropdown)
  // =============================================
  const searchToggle  = document.getElementById('SearchToggle');
  const searchPanel   = document.getElementById('SearchPanel');
  const searchClose   = document.getElementById('SearchPanelClose');
  const searchBackdrop = document.getElementById('SearchBackdrop');

  function openSearch() {
    if (!searchPanel) return;
    // Position panel just below the sticky header
    if (header) {
      searchPanel.style.top = header.offsetHeight + 'px';
      if (searchBackdrop) searchBackdrop.style.top = header.offsetHeight + 'px';
    }
    searchPanel.classList.add('is-open');
    searchPanel.setAttribute('aria-hidden', 'false');
    if (searchBackdrop) searchBackdrop.classList.add('is-open');
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'true');
    const input = searchPanel.querySelector('input[type="search"]');
    if (input) setTimeout(() => input.focus(), 60);
    document.body.style.overflow = 'hidden';
  }

  function closeSearch() {
    if (!searchPanel) return;
    searchPanel.classList.remove('is-open');
    searchPanel.setAttribute('aria-hidden', 'true');
    if (searchBackdrop) searchBackdrop.classList.remove('is-open');
    if (searchToggle) {
      searchToggle.setAttribute('aria-expanded', 'false');
      searchToggle.focus();
    }
    document.body.style.overflow = '';
  }

  if (searchToggle) searchToggle.addEventListener('click', openSearch);
  if (searchClose)  searchClose.addEventListener('click', closeSearch);
  if (searchBackdrop) searchBackdrop.addEventListener('click', closeSearch);

  // =============================================
  // TESTIMONIALS SLIDER
  // =============================================
  const track    = document.getElementById('TestimonialsTrack');
  const prevBtn  = document.getElementById('TestimonialsPrev');
  const nextBtn  = document.getElementById('TestimonialsNext');
  const dotsWrap = document.getElementById('TestimonialsDots');

  if (track) {
    const slides     = Array.from(track.children);
    const totalSlides = slides.length;
    let current      = 0;
    let autoInterval = null;
    let slidesVisible = 1;

    function getSlidesVisible() {
      return 1;
    }

    function getMaxIndex() {
      return Math.max(0, totalSlides - getSlidesVisible());
    }

    function goTo(index) {
      const max = getMaxIndex();
      current   = Math.max(0, Math.min(index, max));

      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === current);
      });

      // Update dots (if present)
      if (dotsWrap) {
        dotsWrap.querySelectorAll('.testimonials__dot').forEach((dot, i) => {
          const isActive = i === current;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      }

      // Update arrow states
      if (prevBtn) prevBtn.disabled = current === 0;
      if (nextBtn) nextBtn.disabled = current >= max;
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }

    function startAuto() {
      const speed = parseInt(
        document.querySelector('[data-autoplay-speed]')?.dataset.autoplajSpeed || '6000'
      );
      autoInterval = setInterval(() => {
        if (current >= getMaxIndex()) {
          goTo(0);
        } else {
          next();
        }
      }, speed);
    }

    function stopAuto() {
      clearInterval(autoInterval);
    }

    if (prevBtn) prevBtn.addEventListener('click', () => { stopAuto(); prev(); startAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { stopAuto(); next(); startAuto(); });

    if (dotsWrap) {
      dotsWrap.querySelectorAll('.testimonials__dot').forEach((dot, i) => {
        dot.addEventListener('click', () => { stopAuto(); goTo(i); startAuto(); });
      });
    }

    // Pause on hover
    track.addEventListener('mouseenter', stopAuto);
    track.addEventListener('mouseleave', startAuto);

    // Touch/swipe support
    let touchStartX = 0;
    track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      stopAuto();
    }, { passive: true });
    track.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 40) {
        diff > 0 ? next() : prev();
      }
      startAuto();
    }, { passive: true });

    // Keyboard navigation inside slider
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    });

    // Init
    window.addEventListener('resize', () => goTo(current), { passive: true });
    goTo(0);
    startAuto();
  }

  // =============================================
  // QUICK ADD TO CART (AJAX)
  // =============================================
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.closest('.product-card__quick-add') && !form.closest('.product-card__atc')) return;

    const btn = form.querySelector('[type="submit"]');
    if (!btn) return;

    // Only intercept single-variant add-to-cart forms
    const hasVariantInput = form.querySelector('input[name="id"]');
    if (!hasVariantInput) return;

    e.preventDefault();

    const originalHTML = btn.innerHTML;
    btn.classList.add('is-adding');
    btn.innerHTML = '<i class="fa-regular fa-spinner fa-spin"></i> Adding...';

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(new FormData(form)),
      });

      if (!response.ok) throw new Error('Add to cart failed');

      // Update cart count in header
      const cartResponse = await fetch('/cart.js');
      const cartData     = await cartResponse.json();
      const countEl      = document.querySelector('.header-cart__count');
      if (countEl) countEl.textContent = cartData.item_count;

      btn.innerHTML = '<i class="fa-regular fa-check"></i> Added!';
      btn.style.background = '#497561';

      setTimeout(() => {
        btn.innerHTML = originalHTML;
        btn.style.background = '';
        btn.classList.remove('is-adding');
      }, 2000);

    } catch (err) {
      console.error(err);
      btn.innerHTML = 'Error — try again';
      btn.classList.remove('is-adding');
      setTimeout(() => { btn.innerHTML = originalHTML; }, 2000);
    }
  });

  // =============================================
  // BACK TO TOP
  // =============================================
  const backToTop = document.getElementById('BackToTop');

  if (backToTop) {
    window.addEventListener('scroll', () => {
      backToTop.classList.toggle('is-visible', window.scrollY > 400);
    }, { passive: true });

    backToTop.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // =============================================
  // COOKIE BAR
  // =============================================
  const cookieBar     = document.getElementById('CookieBar');
  const cookieAccept  = document.getElementById('CookieAccept');
  const cookieDecline = document.getElementById('CookieDecline');

  if (cookieBar && !localStorage.getItem('spm_cookie_consent')) {
    setTimeout(() => cookieBar.classList.add('is-visible'), 800);
  }

  function dismissCookie(choice) {
    localStorage.setItem('spm_cookie_consent', choice);
    cookieBar.classList.remove('is-visible');
  }

  if (cookieAccept)  cookieAccept.addEventListener('click',  () => dismissCookie('accepted'));
  if (cookieDecline) cookieDecline.addEventListener('click', () => dismissCookie('declined'));

  // =============================================
  // FILTER SIDEBAR (collection page)
  // =============================================
  const filterToggle  = document.getElementById('FilterToggle');
  const filterSidebar = document.getElementById('FilterSidebar');
  const filterClose   = document.getElementById('FilterClose');
  const filterOverlay = document.getElementById('FilterOverlay');

  function openFilter() {
    filterSidebar?.classList.add('is-open');
    filterOverlay?.classList.add('is-open');
    filterSidebar?.setAttribute('aria-hidden', 'false');
    filterToggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeFilter() {
    filterSidebar?.classList.remove('is-open');
    filterOverlay?.classList.remove('is-open');
    filterSidebar?.setAttribute('aria-hidden', 'true');
    filterToggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  if (filterToggle) filterToggle.addEventListener('click', openFilter);
  if (filterClose)  filterClose.addEventListener('click', closeFilter);
  if (filterOverlay) filterOverlay.addEventListener('click', closeFilter);

  // Filter group accordion toggles
  document.querySelectorAll('.filter-group__toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const body = document.getElementById(btn.getAttribute('aria-controls'));
      if (body) body.setAttribute('aria-expanded', String(!expanded));
    });
  });

  // Quantity selector buttons (product page)
  document.querySelectorAll('.quantity-selector').forEach((sel) => {
    const input   = sel.querySelector('.quantity-selector__input');
    const minusBtn = sel.querySelectorAll('.quantity-selector__btn')[0];
    const plusBtn  = sel.querySelectorAll('.quantity-selector__btn')[1];

    if (!input) return;

    minusBtn?.addEventListener('click', () => {
      const val = Math.max(parseInt(input.min) || 0, parseInt(input.value) - 1);
      input.value = val;
      input.dispatchEvent(new Event('change'));
    });

    plusBtn?.addEventListener('click', () => {
      const val = parseInt(input.value) + 1;
      input.value = val;
      input.dispatchEvent(new Event('change'));
    });
  });

  // =============================================
  // PRODUCT TABS (product page)
  // =============================================
  document.querySelectorAll('.product-tabs__tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      const panel = document.getElementById(tab.dataset.panel);
      if (!panel) return;

      document.querySelectorAll('.product-tabs__tab').forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      document.querySelectorAll('.product-tabs__panel').forEach((p) => p.classList.remove('is-active'));

      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');
      panel.classList.add('is-active');
    });
  });

  // =============================================
  // PRODUCT GALLERY THUMBNAILS
  // =============================================
  document.querySelectorAll('.product-gallery__thumb').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const mainImg = document.querySelector('.product-gallery__main img');
      if (!mainImg || !thumb.dataset.src) return;

      mainImg.src    = thumb.dataset.src;
      mainImg.srcset = thumb.dataset.srcset || '';

      document.querySelectorAll('.product-gallery__thumb').forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  // =============================================
  // PRODUCT CARD TITLES — force title case
  // Titles are entered in uppercase in the CMS
  // =============================================
  document.querySelectorAll('.product-card__title').forEach((el) => {
    const target = el.querySelector('a') || el;
    target.textContent = target.textContent
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  });

  // =============================================
  // COLLECTION HERO TITLE — force title case
  // Only transforms raw text nodes; leaves visually-hidden span intact
  // =============================================
  document.querySelectorAll('.collection-hero__title').forEach((el) => {
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = node.textContent
          .toLowerCase()
          .replace(/\b\w/g, (l) => l.toUpperCase());
      }
    });
  });

  // =============================================
  // PRODUCT TITLE — force title case
  // =============================================
  document.querySelectorAll('.product-info__title').forEach((el) => {
    el.textContent = el.textContent
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  });

  // =============================================
  // HEADER CART COUNT — sync after product-page AJAX add
  // =============================================
  // product-form.js (Dawn) publishes cartUpdate via pubsub after adding from
  // product pages. pubsub.js is deferred, so subscribe inside DOMContentLoaded.
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
      const sectionHtml = cartData?.sections?.['cart-icon-bubble'];
      if (!sectionHtml) return;

      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const countText = doc.querySelector('[aria-hidden="true"]')?.textContent?.trim() ?? '0';
      const count = parseInt(countText, 10) || 0;

      const countEl = document.querySelector('.header-cart__count');
      if (countEl) countEl.textContent = count;

      const cartLink = document.querySelector('.header-action.header-cart');
      if (cartLink) cartLink.setAttribute('aria-label', `Cart (${count} ${count === 1 ? 'item' : 'items'})`);
    });
  });

})();
